import type {
	AllAgent,
	MyAgent,
} from "@src/components/cards/prompt-card/queries/get-supported-agents";
import { getPromptModelData } from "@src/components/cards/prompt-card/utils/get-prompt-model-data";
import { isImageGenerationModel } from "@src/components/cards/utils/model-presentation";
import { SupportedAgent, type SupportedModel } from "@src/gql/graphql";

export const IMAGE_MODEL_SHORTLIST_SIZE = 3;

export const COMPARE_ALL_3_OPTION_ID = "compare-all-3";
export const SHOW_MORE_MODELS_OPTION_ID = "show-more-models";

export type ImageGenerationModelInfo = {
	model: SupportedModel;
	modelId: string;
	displayName: string;
	preferred: boolean;
	tierAvailable: boolean;
	isRegionRestricted: boolean;
	/** Eligible for in-chat generateImage right now. */
	generationEligible: boolean;
};

export type ImageModelPresentOption = {
	id: string;
	label: string;
	description?: string;
};

export type ImageModelShortlist = {
	models: ImageGenerationModelInfo[];
	total: number;
	offset: number;
	hasMore: boolean;
	nextOffset: number | null;
	/** Ready-to-use presentOptions entries for this page. */
	suggestedPresentOptions: ImageModelPresentOption[];
};

function isEligibleImageModel(model: AllAgent["models"][number]): boolean {
	if (!isImageGenerationModel(model.model)) {
		return false;
	}

	return !model.isRegionRestricted && model.isTierAvailable;
}

/**
 * Lists generation-eligible Image Maker models. Preferred models (from
 * myAgents Image Maker prefs) are ordered first; remaining catalog models
 * follow. Dedupes by modelId.
 */
export function listImageGenerationModels(
	allAgents: AllAgent[] | undefined,
	myAgents: MyAgent[] | undefined,
): ImageGenerationModelInfo[] {
	if (!allAgents || allAgents.length === 0) {
		return [];
	}

	const preferredIds = new Set<string>();
	const imageMakerMine = myAgents?.find(
		(agent) => agent.agent === SupportedAgent.ImageMaker,
	);
	if (imageMakerMine) {
		for (const model of imageMakerMine.models) {
			if (isEligibleImageModel(model)) {
				preferredIds.add(model.modelId);
			}
		}
	}

	const byModelId = new Map<string, ImageGenerationModelInfo>();

	for (const agent of allAgents) {
		for (const model of agent.models) {
			if (!isEligibleImageModel(model)) {
				continue;
			}

			if (byModelId.has(model.modelId)) {
				continue;
			}

			const preferred = preferredIds.has(model.modelId);
			byModelId.set(model.modelId, {
				model: model.model,
				modelId: model.modelId,
				displayName: getPromptModelData(model.model).name,
				preferred,
				tierAvailable: model.isTierAvailable,
				isRegionRestricted: model.isRegionRestricted,
				generationEligible: true,
			});
		}
	}

	const all = [...byModelId.values()];
	all.sort((a, b) => {
		if (a.preferred !== b.preferred) {
			return a.preferred ? -1 : 1;
		}
		return a.displayName.localeCompare(b.displayName);
	});

	return all;
}

export function pickImageModelShortlist(
	models: ImageGenerationModelInfo[],
	options: { offset?: number; limit?: number } = {},
): ImageModelShortlist {
	const offset = Math.max(0, options.offset ?? 0);
	const limit = Math.max(1, options.limit ?? IMAGE_MODEL_SHORTLIST_SIZE);
	const page = models.slice(offset, offset + limit);
	const nextOffset = offset + page.length;
	const hasMore = nextOffset < models.length;

	const suggestedPresentOptions: ImageModelPresentOption[] = page.map(
		(model) => ({
			// Use gateway modelId so presentOptions → generateImageInChat
			// needs no enum→id remapping.
			id: model.modelId,
			label: model.displayName,
			description: model.preferred
				? `Preferred · gateway id ${model.modelId}`
				: `Gateway id ${model.modelId}`,
		}),
	);

	if (page.length >= 2) {
		suggestedPresentOptions.push({
			id: COMPARE_ALL_3_OPTION_ID,
			label: page.length === 2 ? "Compare both" : `Compare all ${page.length}`,
			description: `Generate with ${page
				.map((m) => m.displayName)
				.join(", ")} in parallel`,
		});
	}

	if (hasMore) {
		suggestedPresentOptions.push({
			id: SHOW_MORE_MODELS_OPTION_ID,
			label: "Show more models",
			description: `Next models starting at offset ${nextOffset}`,
		});
	}

	return {
		models: page,
		total: models.length,
		offset,
		hasMore,
		nextOffset: hasMore ? nextOffset : null,
		suggestedPresentOptions,
	};
}

export function isReservedImageOptionId(optionId: string): boolean {
	return (
		optionId === COMPARE_ALL_3_OPTION_ID ||
		optionId === SHOW_MORE_MODELS_OPTION_ID
	);
}
